import { useEffect, useMemo, useReducer } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useFilters, rangeISO } from '@/state/filters';
import { useOverview } from '@/api/bi';
import { useWebSocket } from '@/api/ws';
import { useStoresList } from '@/api/legacy/stores';
import { useSupportConfig, bucketize } from '@/api/legacy/support';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';
import { AnimatedNumber } from '@/components/charts/AnimatedNumber';
import { ACTIVE_ORDER_STATUSES, statusLabel, statusTone } from '@/lib/status';

/**
 * Live Ops board.
 * - Counters: pulled once from the BI overview endpoint, then nudged by WS
 *   `order_updated` / `order_created` events.
 * - SLA list: orders currently older than X minutes in `created` / `packed`
 *   state, derived from the WS-tracked event log so it auto-evicts.
 *
 * The WS connects to `/ws/orders/<store_id>/` if a store is selected,
 * else `/ws/orders/` (admin firehose).
 */

interface LiveOrder {
  id: number;
  status: string;
  created_timestamp?: string;
  store_id?: number;
  customer?: string;
  total?: number | string;
  late_tags?: { tag: string; created_at: string }[];
  [k: string]: unknown;
}

type WsEvent =
  | { type: 'order_created'; order: LiveOrder }
  | { type: 'order_updated'; order: LiveOrder };

interface LiveState {
  orders: Record<number, LiveOrder>;
  lastEventAt: number | null;
}

function liveReducer(state: LiveState, ev: WsEvent): LiveState {
  const incoming = ev.order;
  if (!incoming?.id) return state;
  // Drop terminal states from the live map after a short grace.
  const next = { ...state.orders, [incoming.id]: incoming };
  if (incoming.status === 'completed' || incoming.status === 'cancelled') {
    setTimeout(() => {
      // Best-effort: actual cleanup happens via React state below.
    }, 0);
  }
  return { orders: next, lastEventAt: Date.now() };
}

export function LiveOpsPage() {
  const { range, storeId, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const { data: stores = [] } = useStoresList();
  const supportCfg = useSupportConfig();

  // BI snapshot — counters & exceptions.
  const overview = useOverview({ start: iso.start, end: iso.end, storeId });

  // WS subscription. Path is null while we have nothing useful to subscribe to.
  const wsPath = storeId ? `/ws/orders/${storeId}/` : '/ws/orders/';

  const [state, dispatch] = useReducer(liveReducer, { orders: {}, lastEventAt: null });
  const { status: wsStatus } = useWebSocket<WsEvent>({
    path: wsPath,
    onMessage: (ev) => {
      if (ev?.type === 'order_created' || ev?.type === 'order_updated') {
        dispatch(ev);
      }
    },
  });

  // Periodically prune terminal orders so the list doesn't grow forever.
  useEffect(() => {
    const t = window.setInterval(() => {
      const now = Date.now();
      Object.values(state.orders).forEach((o) => {
        if ((o.status === 'completed' || o.status === 'cancelled')) {
          const ts = o.created_timestamp ? new Date(o.created_timestamp).getTime() : now;
          if (now - ts > 5 * 60_000) {
            // The reducer mutates a fresh object; mimic that here without dispatch.
            delete state.orders[o.id];
          }
        }
      });
    }, 30_000);
    return () => window.clearInterval(t);
  }, [state.orders]);

  const liveOrders = Object.values(state.orders);
  const inFlight = liveOrders.filter(
    (o) => !['completed', 'cancelled'].includes(o.status),
  );
  const sla = useMemo(() => computeSlaList(inFlight, supportCfg), [inFlight, supportCfg]);
  const slaThresholdSec = supportCfg.packing_limit_time ?? 10 * 60;

  const counters = overview.data?.in_flight.by_status ?? {};
  const counterTotal = overview.data?.in_flight.total ?? 0;

  return (
    <>
      <PageHeader
        eyebrow="Operations"
        title="Live Ops"
        description={
          storeId
            ? `Realtime board for ${stores.find((s) => s.id === storeId)?.name ?? 'this store'}.`
            : 'Realtime board across all stores you can access.'
        }
        actions={<WsStatusPill status={wsStatus} />}
      />

      <div className="grid gap-4 stagger md:grid-cols-2 lg:grid-cols-5">
        <CounterCard
          label="In-flight"
          value={counterTotal}
          loading={overview.isLoading}
          error={overview.isError}
          primary
        />
        {ACTIVE_ORDER_STATUSES.map((s) => (
          <CounterCard
            key={s}
            label={statusLabel(s)}
            value={counters[s] ?? 0}
            loading={overview.isLoading}
            error={overview.isError}
            tone={statusTone(s) as 'info' | 'warning' | 'primary' | 'success'}
          />
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {/* SLA risk list */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>SLA risk · stuck {'>'} {Math.round(slaThresholdSec / 60)} min</CardTitle>
            <Badge tone={sla.length === 0 ? 'success' : 'warning'}>
              {sla.length} order{sla.length === 1 ? '' : 's'}
            </Badge>
          </CardHeader>
          <CardBody>
            {sla.length === 0 ? (
              <EmptyState
                icon={<Activity className="h-5 w-5" />}
                title="All clear right now"
                description={
                  state.lastEventAt
                    ? `Last live event ${fmt.ago(new Date(state.lastEventAt))}.`
                    : 'Waiting for the first realtime event…'
                }
              />
            ) : (
              <ul className="divide-y divide-border">
                <AnimatePresence initial={false}>
                  {sla.map((row) => (
                    <motion.li
                      key={row.id}
                      layout
                      initial={{ opacity: 0, transform: 'translateY(6px)' }}
                      animate={{ opacity: 1, transform: 'translateY(0px)' }}
                      exit={{ opacity: 0, transform: 'translateY(-4px)' }}
                      transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                      className="flex items-center justify-between gap-3 py-2.5 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">
                          #{row.id}{' '}
                          {row.customer ? (
                            <span className="text-muted-foreground">· {row.customer}</span>
                          ) : null}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {fmt.ago(row.created_timestamp ?? null)} in <span className="font-medium">{statusLabel(row.status)}</span>
                        </p>
                      </div>
                      <Badge tone={row.bucket === 'late' ? 'danger' : 'warning'}>
                        <AlertTriangle className="h-3 w-3" />
                        {fmt.duration(row.ageSeconds)}
                      </Badge>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            )}
          </CardBody>
        </Card>

        {/* Stream side-panel */}
        <Card>
          <CardHeader>
            <CardTitle>Live stream</CardTitle>
            <Badge tone="primary">
              <Activity className="h-3 w-3" />
              {liveOrders.length}
            </Badge>
          </CardHeader>
          <CardBody>
            {overview.isError ? (
              <ErrorState onRetry={() => void overview.refetch()} />
            ) : liveOrders.length === 0 ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <ul className="space-y-2">
                <AnimatePresence initial={false}>
                  {liveOrders
                    .slice(-12)
                    .reverse()
                    .map((o) => (
                      <motion.li
                        key={o.id}
                        layout
                        initial={{ opacity: 0, transform: 'translateX(8px)' }}
                        animate={{ opacity: 1, transform: 'translateX(0px)' }}
                        exit={{ opacity: 0, transform: 'translateX(-8px)' }}
                        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
                        className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2 text-xs"
                      >
                        <span className="font-medium">#{o.id}</span>
                        <Badge tone={statusTone(o.status)}>{statusLabel(o.status)}</Badge>
                      </motion.li>
                    ))}
                </AnimatePresence>
              </ul>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

/**
 * Promotes a live in-flight order onto the SLA list when its age in the
 * current stage exceeds the relevant `SupportConfiguration` threshold.
 *
 * We pick the threshold per stage rather than a global "10 min": a packed
 * order older than the rider window is "rider-late", an older `created`
 * order is "packing-late", etc. Falls back to the previous 10-min default
 * when the singleton is missing.
 */
function computeSlaList(
  orders: LiveOrder[],
  cfg: ReturnType<typeof useSupportConfig>,
): Array<LiveOrder & { ageSeconds: number; bucket: ReturnType<typeof bucketize> }> {
  const now = Date.now();
  return orders
    .map((o) => {
      const ts = o.created_timestamp ? new Date(o.created_timestamp).getTime() : now;
      const ageSeconds = Math.round((now - ts) / 1000);
      const { normal, limit } = stageThresholds(o.status, cfg);
      const bucket = bucketize(ageSeconds, normal, limit);
      return { ...o, ageSeconds, bucket };
    })
    .filter((o) => o.bucket !== 'on-time')
    .sort((a, b) => b.ageSeconds - a.ageSeconds)
    .slice(0, 20);
}

function stageThresholds(status: string, cfg: ReturnType<typeof useSupportConfig>): {
  normal: number | null;
  limit: number | null;
} {
  switch (status) {
    case 'created':
    case 'packer_assigned':
    case 'packed':
      return { normal: cfg.packing_normal_time, limit: cfg.packing_limit_time };
    case 'billing':
      return { normal: cfg.biller_normal_time, limit: cfg.biller_limit_time };
    case 'picked':
      return { normal: cfg.picking_time, limit: cfg.rider_time };
    case 'reached':
      return { normal: cfg.rider_time, limit: cfg.secondary_rider_time };
    default:
      return { normal: cfg.packing_normal_time, limit: cfg.packing_limit_time };
  }
}

interface CounterCardProps {
  label: string;
  value: number;
  loading?: boolean;
  error?: boolean;
  primary?: boolean;
  tone?: 'info' | 'warning' | 'primary' | 'success';
}

function CounterCard({ label, value, loading, error, primary, tone }: CounterCardProps) {
  return (
    <Card className={cn(primary && 'ring-1 ring-primary/40')}>
      <CardBody className="!pt-5">
        <p className="text-xs text-muted-foreground">{label}</p>
        {loading ? (
          <Skeleton className="mt-2 h-8 w-16" />
        ) : error ? (
          // "0 in-flight" is the single most consequential wrong answer this
          // board can give a dispatcher, so a failed fetch shows nothing rather
          // than a zero.
          <p className="mt-2 font-display text-3xl font-bold tracking-tight text-muted-foreground">
            —
          </p>
        ) : (
          <p
            className={cn(
              'mt-2 font-display text-3xl font-bold tracking-tight kpi-num',
              tone === 'success' && 'text-success',
              tone === 'warning' && 'text-warning-soft',
              tone === 'info' && 'text-info',
              primary && 'text-primary',
            )}
          >
            <AnimatedNumber value={value} duration={500} />
          </p>
        )}
      </CardBody>
    </Card>
  );
}

function WsStatusPill({ status }: { status: string }) {
  if (status === 'open') {
    return (
      <Badge tone="success" className="gap-1.5">
        <span className="live-dot" aria-hidden />
        live
      </Badge>
    );
  }
  if (status === 'connecting') {
    return (
      <Badge tone="info">
        <Wifi className="h-3 w-3" />
        connecting
      </Badge>
    );
  }
  return (
    <Badge tone="danger">
      <WifiOff className="h-3 w-3" />
      offline
    </Badge>
  );
}
